import { useState, useMemo, useCallback, useEffect } from 'react';
import { Combobox } from '@headlessui/react';
import { Check, ChevronsUpDown, MapPin, Loader2, X, Home } from 'lucide-react';
import { REGIONS } from '../../data/mockData';
import { useI18n } from '../../i18n';
import { LocationFilter, Property, Auction } from '../../types';

// Demand type for location search
interface DemandLocation {
  id: string;
  location: {
    region: string;
    city: string;
    address?: string;
    lat?: number;
    lng?: number;
  };
}

interface MultiLocationSearchProps {
  values: LocationFilter[];
  onChange: (values: LocationFilter[]) => void;
  className?: string;
  placeholder?: string;
  showOsmSearch?: boolean;
  maxLocations?: number;
  /** Active listings to include their addresses in search */
  listings?: (Property | Auction | DemandLocation)[];
  /** Page type for context */
  pageType?: 'offers' | 'demands' | 'auctions';
}

interface LocationOption {
  id: string;
  name: string;
  type: 'region' | 'subregion' | 'osm' | 'listing-address';
  parent?: string;
  lat?: number;
  lng?: number;
  zoom?: number;
  displayName?: string;
  propertyId?: string;
  subregion?: string;
}

export function MultiLocationSearch({ 
  values = [], 
  onChange, 
  className, 
  placeholder, 
  showOsmSearch = true,
  maxLocations = 5,
  listings = [],
  pageType
}: MultiLocationSearchProps) {
  const [query, setQuery] = useState('');
  const [osmResults, setOsmResults] = useState<LocationOption[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const { t, language } = useI18n();

  // Lokalne lokacije iz mock podataka
  const localLocations = useMemo(() => {
    const list: LocationOption[] = [];
    REGIONS.forEach(region => {
      list.push({ 
        id: `r-${region.id}`, 
        name: region.name, 
        type: 'region',
        lat: region.lat,
        lng: region.lng,
        zoom: region.zoom || 10
      });
      region.cities.forEach(city => {
        list.push({
          id: `c-${city.id}`,
          name: city.name,
          type: 'subregion',
          parent: region.name,
          lat: city.lat,
          lng: city.lng,
          zoom: 13
        });
      });
    });
    return list;
  }, []);

  // Listing addresses from active listings
  const listingAddresses = useMemo(() => {
    const addresses: LocationOption[] = [];
    const seenAddresses = new Set<string>();
    
    listings.forEach(listing => {
      if (listing.location?.address && listing.location.lat && listing.location.lng) {
        const addressKey = listing.location.address.toLowerCase();
        if (!seenAddresses.has(addressKey)) {
          seenAddresses.add(addressKey);
          addresses.push({
            id: `listing-${listing.id}`,
            name: listing.location.address,
            displayName: `${listing.location.address}, ${listing.location.city}`,
            type: 'listing-address',
            parent: listing.location.city,
            lat: listing.location.lat,
            lng: listing.location.lng,
            zoom: 17,
            propertyId: listing.id,
            subregion: listing.location.city
          });
        }
      }
    });
    
    return addresses;
  }, [listings]);

  // OSM Nominatim pretraga
  const searchOsm = useCallback(async (searchQuery: string) => {
    if (!showOsmSearch || searchQuery.length < 3) {
      setOsmResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&countrycodes=rs&limit=5&addressdetails=1`,
        {
          headers: {
            'Accept-Language': language === 'sr' ? 'sr,sr-RS' : 'en',
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        const results: LocationOption[] = data.map((item: any, index: number) => {
          // Extract city/municipality from address details for subregion
          const address = item.address || {};
          const subregion = address.city || address.town || address.municipality || address.village || address.county || '';
          
          return {
            id: `osm-${item.place_id || index}`,
            name: item.display_name.split(',')[0],
            displayName: item.display_name,
            type: 'osm' as const,
            lat: parseFloat(item.lat),
            lng: parseFloat(item.lon),
            zoom: 15,
            subregion: subregion, // Store the subregion for filtering
          };
        });
        setOsmResults(results);
      }
    } catch (error) {
      console.error('OSM search error:', error);
      setOsmResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [showOsmSearch, language]);

  // Debounced OSM pretraga
  useEffect(() => {
    const timer = setTimeout(() => {
      if (query.length >= 3) {
        searchOsm(query);
      } else {
        setOsmResults([]);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query, searchOsm]);

  // Filtrirane lokalne lokacije (exclude already selected)
  const selectedNames = values.map(v => v.name);
  const selectedPropertyIds = values.filter(v => v.propertyId).map(v => v.propertyId);
  
  const filteredLocalLocations = (query === ''
    ? localLocations
    : localLocations.filter((location) =>
        location.name.toLowerCase().includes(query.toLowerCase())
      )
  ).filter(loc => !selectedNames.includes(loc.name));

  // Filtered listing addresses
  const filteredListingAddresses = useMemo(() => {
    if (query.length < 2) return [];
    return listingAddresses.filter(addr => {
      const matchesQuery = addr.name.toLowerCase().includes(query.toLowerCase()) ||
        (addr.displayName && addr.displayName.toLowerCase().includes(query.toLowerCase()));
      const notSelected = !selectedPropertyIds.includes(addr.propertyId);
      return matchesQuery && notSelected;
    });
  }, [query, listingAddresses, selectedPropertyIds]);

  // Kombinovani rezultati
  const allResults = useMemo(() => {
    const results: LocationOption[] = [];

    if (filteredLocalLocations.length > 0) {
      results.push(...filteredLocalLocations.slice(0, 10));
    }

    // Add listing addresses (show these before OSM results)
    if (filteredListingAddresses.length > 0) {
      results.push(...filteredListingAddresses.slice(0, 5));
    }

    if (osmResults.length > 0) {
      // Exclude already selected OSM results
      const filteredOsm = osmResults.filter(osm => 
        !selectedNames.includes(osm.displayName || osm.name)
      );
      results.push(...filteredOsm);
    }

    return results;
  }, [filteredLocalLocations, filteredListingAddresses, osmResults, selectedNames]);

  const handleSelect = (selectedName: string) => {
    if (values.length >= maxLocations) return;
    
    const selectedLocation = allResults.find(loc => {
      if (loc.type === 'osm' || loc.type === 'listing-address') {
        return loc.displayName === selectedName || loc.name === selectedName;
      }
      return loc.name === selectedName;
    });

    if (selectedLocation) {
      // Determine the type for LocationFilter
      let locationType: LocationFilter['type'];
      if (selectedLocation.type === 'osm') {
        locationType = 'address';
      } else {
        locationType = selectedLocation.type;
      }

      const newLocation: LocationFilter = {
        name: (selectedLocation.type === 'osm' || selectedLocation.type === 'listing-address')
          ? (selectedLocation.displayName || selectedLocation.name)
          : selectedLocation.name,
        type: locationType,
        region: selectedLocation.type === 'subregion' ? selectedLocation.parent : undefined,
        subregion: selectedLocation.subregion, // City/municipality for address lookups
        lat: selectedLocation.lat,
        lng: selectedLocation.lng,
        zoom: selectedLocation.zoom,
        propertyId: selectedLocation.propertyId, // For listing-address type
      };
      
      onChange([...values, newLocation]);
      setQuery('');
    }
  };

  const handleRemove = (index: number) => {
    const newValues = values.filter((_, i) => i !== index);
    onChange(newValues);
  };

  const canAddMore = values.length < maxLocations;

  return (
    <div className={`space-y-2 ${className}`}>
      {/* Selected locations */}
      {values.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {values.map((location, index) => (
            <div
              key={`${location.name}-${index}`}
              className={`flex items-center gap-1 px-2 py-1 rounded-lg text-sm ${
                location.type === 'listing-address' 
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200'
                  : 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200'
              }`}
            >
              {location.type === 'listing-address' && <Home className="w-3 h-3" />}
              {location.type === 'address' && <MapPin className="w-3 h-3" />}
              <span className="truncate max-w-[150px]" title={location.name}>
                {location.name.split(',')[0]}
              </span>
              <button
                onClick={() => handleRemove(index)}
                className="ml-1 hover:opacity-70"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Search input */}
      {canAddMore && (
        <Combobox value="" onChange={handleSelect}>
          <div className="relative">
            <div className="relative w-full cursor-default overflow-hidden rounded-lg bg-white dark:bg-slate-700 text-left shadow-sm focus:outline-none border border-slate-300 dark:border-slate-600">
              <Combobox.Input
                className="w-full border-none py-2.5 pl-3 pr-10 text-sm leading-5 text-slate-900 dark:text-white bg-transparent focus:ring-0 outline-none placeholder-slate-400"
                displayValue={() => query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={placeholder || (language === 'sr' 
                  ? `Dodaj lokaciju (${values.length}/${maxLocations})...` 
                  : `Add location (${values.length}/${maxLocations})...`)}
              />
              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                {isSearching ? (
                  <Loader2 className="h-4 w-4 text-slate-400 animate-spin" aria-hidden="true" />
                ) : (
                  <ChevronsUpDown className="h-4 w-4 text-slate-400" aria-hidden="true" />
                )}
              </Combobox.Button>
            </div>
            <Combobox.Options className="absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-slate-800 py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm z-50">
              {allResults.length === 0 && query !== '' && !isSearching ? (
                <div className="relative cursor-default select-none px-4 py-2 text-slate-500 dark:text-slate-400">
                  {language === 'sr' ? 'Nema rezultata.' : 'No results.'}
                </div>
              ) : (
                <>
                  {/* Lokalni rezultati */}
                  {filteredLocalLocations.length > 0 && (
                    <>
                      <div className="px-3 py-1 text-xs font-semibold text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-700">
                        {language === 'sr' ? 'Regioni i podregioni' : 'Regions and subregions'}
                      </div>
                      {filteredLocalLocations.slice(0, 10).map((location) => (
                        <Combobox.Option
                          key={location.id}
                          className={({ active }) =>
                            `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                              active ? 'bg-amber-500 text-white' : 'text-slate-900 dark:text-white'
                            }`
                          }
                          value={location.name}
                        >
                          {({ selected, active }) => (
                            <>
                              <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                {location.name}
                                {location.type === 'subregion' && (
                                  <span className={`text-xs ml-1 ${active ? 'text-amber-100' : 'text-slate-500 dark:text-slate-400'}`}>
                                    ({location.parent})
                                  </span>
                                )}
                              </span>
                              {selected && (
                                <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-amber-600'}`}>
                                  <Check className="h-5 w-5" aria-hidden="true" />
                                </span>
                              )}
                            </>
                          )}
                        </Combobox.Option>
                      ))}
                    </>
                  )}

                  {/* Adrese iz oglasa */}
                  {filteredListingAddresses.length > 0 && (
                    <>
                      <div className="px-3 py-1 text-xs font-semibold text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 mt-1">
                        {language === 'sr' ? 'Adrese iz oglasa' : 'Listing addresses'}
                      </div>
                      {filteredListingAddresses.slice(0, 5).map((location) => (
                        <Combobox.Option
                          key={location.id}
                          className={({ active }) =>
                            `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                              active ? 'bg-green-500 text-white' : 'text-slate-900 dark:text-white'
                            }`
                          }
                          value={location.displayName || location.name}
                        >
                          {({ active }) => (
                            <>
                              <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                                <Home className={`h-4 w-4 ${active ? 'text-white' : 'text-green-500'}`} aria-hidden="true" />
                              </span>
                              <span className="block truncate text-sm">
                                {location.displayName || location.name}
                              </span>
                            </>
                          )}
                        </Combobox.Option>
                      ))}
                    </>
                  )}

                  {/* OSM rezultati */}
                  {osmResults.length > 0 && (
                    <>
                      <div className="px-3 py-1 text-xs font-semibold text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-700 mt-1">
                        {language === 'sr' ? 'Ulice i adrese' : 'Streets and addresses'}
                      </div>
                      {osmResults.filter(osm => !selectedNames.includes(osm.displayName || osm.name)).map((location) => (
                        <Combobox.Option
                          key={location.id}
                          className={({ active }) =>
                            `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                              active ? 'bg-amber-500 text-white' : 'text-slate-900 dark:text-white'
                            }`
                          }
                          value={location.displayName || location.name}
                        >
                          {({ active }) => (
                            <>
                              <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                                <MapPin className={`h-4 w-4 ${active ? 'text-white' : 'text-slate-400'}`} aria-hidden="true" />
                              </span>
                              <span className="block truncate text-sm">
                                {location.displayName || location.name}
                              </span>
                            </>
                          )}
                        </Combobox.Option>
                      ))}
                    </>
                  )}

                  {isSearching && (
                    <div className="relative cursor-default select-none px-4 py-2 text-slate-500 dark:text-slate-400 flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {language === 'sr' ? 'Pretražujem...' : 'Searching...'}
                    </div>
                  )}
                </>
              )}
            </Combobox.Options>
          </div>
        </Combobox>
      )}

      {!canAddMore && (
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {language === 'sr' 
            ? `Maksimalan broj lokacija (${maxLocations}) je dostignut.`
            : `Maximum locations (${maxLocations}) reached.`}
        </p>
      )}
    </div>
  );
}
